import { forwardRef, useId, useState } from 'react';
import { Eye, EyeOff, Search } from 'lucide-react';
import { cn } from '@/lib/cn';

const controlBase =
  'w-full rounded-md border border-[color:var(--ts-input)] bg-surface px-3 text-base text-gray-3 ' +
  'placeholder:text-[color:var(--ts-placeholder)] ' +
  'transition-[border-color,box-shadow,background-color] duration-150 ease-out ' +
  'hover:border-[color:var(--ts-input-hover)] ' +
  'focus:border-primary focus:shadow-[0_0_0_3px_var(--ts-focus-ring-soft)] focus:outline-none ' +
  'aria-invalid:border-destructive aria-invalid:hover:border-destructive ' +
  'aria-invalid:focus:border-destructive aria-invalid:focus:shadow-[0_0_0_3px_rgba(180,35,35,0.15)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[color:var(--ts-input)]';

export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...props },
  ref,
) {
  return <input ref={ref} className={cn(controlBase, 'h-10', className)} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    return <textarea ref={ref} className={cn(controlBase, 'min-h-28 py-2.5 font-mono text-sm', className)} {...props} />;
  },
);

/** Password field with the portal's eye toggle on the right. */
export const PasswordInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function PasswordInput({ className, ...props }, ref) {
    const [shown, setShown] = useState(false);
    return (
      <span className="relative block">
        <input
          ref={ref}
          type={shown ? 'text' : 'password'}
          className={cn(controlBase, 'h-10 pr-11', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setShown((value) => !value)}
          aria-label={shown ? 'Hide the password' : 'Show the password'}
          className={cn(
            'absolute top-1/2 right-2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full',
            'text-gray-1 transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)] hover:text-primary',
          )}
        >
          {shown ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        </button>
      </span>
    );
  },
);

/** The toolbar search box: 44px, white, bordered, search icon on the left. */
export const SearchInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function SearchInput({ className, placeholder = 'Search', ...props }, ref) {
    return (
      <span className="relative block w-full">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-1"
          aria-hidden="true"
        />
        <input
          ref={ref}
          type="search"
          placeholder={placeholder}
          className={cn(controlBase, 'h-10 pl-10', className)}
          {...props}
        />
      </span>
    );
  },
);

export interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  className?: string;
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => React.ReactNode;
}

/** Label above in 14px medium, control, then the error in 12px red. */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className={cn('w-full min-w-0', className)}>
      <label htmlFor={id} className="mb-2 block text-base font-medium text-gray-3">
        {label}
        {required ? <span className="ml-1 text-destructive">*</span> : null}
      </label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
